import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';

const EDGE_THRESHOLD_PX = 100;
const SCROLL_SPEED_PX = 8;

const useAutoScrollOnDrag = (
  containerRef: React.RefObject<HTMLElement | null>
): {
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: () => void;
  onDragEnd: () => void;
} => {
  const animationFrameRef = useRef<number | null>(null);

  const stopScrolling = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      const container = containerRef.current;

      if (!container) return;

      const rect = container.getBoundingClientRect();
      const cursorY = e.clientY;
      const distanceFromTop = cursorY - rect.top;
      const distanceFromBottom = rect.bottom - cursorY;

      stopScrolling();

      if (distanceFromTop < EDGE_THRESHOLD_PX) {
        const scroll = (): void => {
          container.scrollTop -= SCROLL_SPEED_PX;
          animationFrameRef.current = requestAnimationFrame(scroll);
        };

        animationFrameRef.current = requestAnimationFrame(scroll);
      } else if (distanceFromBottom < EDGE_THRESHOLD_PX) {
        const scroll = (): void => {
          container.scrollTop += SCROLL_SPEED_PX;
          animationFrameRef.current = requestAnimationFrame(scroll);
        };

        animationFrameRef.current = requestAnimationFrame(scroll);
      }
    },
    [containerRef, stopScrolling]
  );

  useEffect(() => stopScrolling, [stopScrolling]);

  return {
    onDragOver,
    onDragLeave: stopScrolling,
    onDrop: stopScrolling,
    onDragEnd: stopScrolling,
  };
};

export default useAutoScrollOnDrag;
